import React, { useState, useEffect } from 'react';
import styles from './CookieConsent.module.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = localStorage.getItem('aurelia-cookie-consent');
    if (!cookieConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedSettings = JSON.parse(cookieConsent);
      setCookieSettings(savedSettings);
      initializeCookies(savedSettings);
    }
  }, []);

  const initializeCookies = (settings) => {
    // Initialize cookies based on user preferences
    if (settings.analytics && window.gtag) {
      // Enable Google Analytics
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }

    if (settings.marketing) {
      // Enable marketing cookies
      window.gtag('consent', 'update', {
        ad_storage: 'granted'
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    setCookieSettings(allAccepted);
    localStorage.setItem('aurelia-cookie-consent', JSON.stringify(allAccepted));
    initializeCookies(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    setCookieSettings(necessaryOnly);
    localStorage.setItem('aurelia-cookie-consent', JSON.stringify(necessaryOnly));
    initializeCookies(necessaryOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleCustomize = () => {
    setShowSettings(true);
  };

  const handleSaveCustom = () => {
    localStorage.setItem('aurelia-cookie-consent', JSON.stringify(cookieSettings));
    initializeCookies(cookieSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSettingChange = (setting) => {
    if (setting === 'necessary') return; // Necessary cookies can't be disabled
    
    setCookieSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className={styles.cookieConsentBackdrop} />
      <div className={styles.cookieConsentBanner}>
        <div className={styles.cookieConsentContent}>
          {!showSettings ? (
            <>
              <div className={styles.cookieConsentText}>
                <h3>We Value Your Privacy</h3>
                <p>
                  We use cookies and similar technologies to enhance your experience on our website, 
                  analyze site traffic, and personalize content. By clicking "Accept All", you consent 
                  to our use of cookies in accordance with Nigeria's Data Protection Regulation (NDPR) 
                  and as described in our{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>.
                </p>
              </div>
              <div className={styles.cookieConsentActions}>
                <button 
                  className={`${styles.cookieBtn} ${styles.cookieBtnSecondary}`}
                  onClick={handleAcceptNecessary}
                >
                  Necessary Only
                </button>
                <button 
                  className={`${styles.cookieBtn} ${styles.cookieBtnOutline}`}
                  onClick={handleCustomize}
                >
                  Customize
                </button>
                <button 
                  className={`${styles.cookieBtn} ${styles.cookieBtnPrimary}`}
                  onClick={handleAcceptAll}
                >
                  Accept All
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.cookieSettings}>
                <h3>Cookie Preferences</h3>
                <p>Choose which cookies you want to allow. You can change these settings at any time.</p>
                
                <div className={styles.cookieCategory}>
                  <div className={styles.cookieCategoryHeader}>
                    <label className={styles.cookieToggle}>
                      <input
                        type="checkbox"
                        checked={cookieSettings.necessary}
                        onChange={() => handleSettingChange('necessary')}
                        disabled
                      />
                      <span className={styles.cookieSlider}></span>
                      <strong>Necessary Cookies</strong>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    These cookies are essential for the website to function properly. 
                    They enable basic features like page navigation and access to secure areas.
                  </p>
                </div>

                <div className={styles.cookieCategory}>
                  <div className={styles.cookieCategoryHeader}>
                    <label className={styles.cookieToggle}>
                      <input
                        type="checkbox"
                        checked={cookieSettings.analytics}
                        onChange={() => handleSettingChange('analytics')}
                      />
                      <span className={styles.cookieSlider}></span>
                      <strong>Analytics Cookies</strong>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    These cookies help us understand how visitors interact with our website 
                    by collecting and reporting information anonymously.
                  </p>
                </div>

                <div className={styles.cookieCategory}>
                  <div className={styles.cookieCategoryHeader}>
                    <label className={styles.cookieToggle}>
                      <input
                        type="checkbox"
                        checked={cookieSettings.marketing}
                        onChange={() => handleSettingChange('marketing')}
                      />
                      <span className={styles.cookieSlider}></span>
                      <strong>Marketing Cookies</strong>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    These cookies are used to deliver more relevant advertisements 
                    and track the effectiveness of our marketing campaigns.
                  </p>
                </div>

                <div className={styles.cookieCategory}>
                  <div className={styles.cookieCategoryHeader}>
                    <label className={styles.cookieToggle}>
                      <input
                        type="checkbox"
                        checked={cookieSettings.preferences}
                        onChange={() => handleSettingChange('preferences')}
                      />
                      <span className={styles.cookieSlider}></span>
                      <strong>Preference Cookies</strong>
                    </label>
                  </div>
                  <p className={styles.cookieDescription}>
                    These cookies remember your choices and preferences to provide 
                    you with a more personalized experience.
                  </p>
                </div>
              </div>
              
              <div className={styles.cookieConsentActions}>
                <button 
                  className={`${styles.cookieBtn} ${styles.cookieBtnOutline}`}
                  onClick={() => setShowSettings(false)}
                >
                  Back
                </button>
                <button 
                  className={`${styles.cookieBtn} ${styles.cookieBtnPrimary}`}
                  onClick={handleSaveCustom}
                >
                  Save Preferences
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;